import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { User } from 'app/core/user/user.types';
import { now } from 'lodash';
import { of, map, Observable, ReplaySubject, tap, BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class UserService {
    private _user: ReplaySubject<User> = new ReplaySubject<User>(1);
    private _externalUser: BehaviorSubject<{ name: string; email: string; administrador_backlog?: any } | null> = new BehaviorSubject<{ name: string; email: string; administrador_backlog?: any } | null>(null);

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {
        const stored = this.getExternalUser();
        this._externalUser.next(stored);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    private _isEmbedded(): boolean {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    }

    getExternalUser(): { name: string; email: string; administrador_backlog?: any } | null {
        const stored = this._isEmbedded()
            ? sessionStorage.getItem('external_user')
            : localStorage.getItem('external_user');
        if (stored) {
            try {
                return JSON.parse(stored);
            } catch (e) {
                return null;
            }
        }
        return null;
    }

    get externalUser$(): Observable<{ name: string; email: string; administrador_backlog?: any } | null> {
        return this._externalUser.asObservable();
    }

    set externalUser(value: { name: string; email: string; administrador_backlog?: any } | null) {
        if (value) {
            if (this._isEmbedded()) {
                sessionStorage.setItem('external_user', JSON.stringify(value));
            } else {
                localStorage.setItem('external_user', JSON.stringify(value));
            }
        } else {
            if (this._isEmbedded()) {
                sessionStorage.removeItem('external_user');
            } else {
                localStorage.removeItem('external_user');
            }
        }
        this._externalUser.next(value);
    }

    set user(user: User) {
        if (this._isEmbedded()) {
            sessionStorage.setItem('user', JSON.stringify(user));
        } else {
            localStorage.setItem('user', JSON.stringify(user));
        }
    }

    get user$(): Observable<User> {
        const storedUser = this._isEmbedded()
            ? sessionStorage.getItem('user')
            : localStorage.getItem('user');
        return of(JSON.parse(storedUser ?? ''));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get the current logged in user data
     */
    get(): Observable<User> {
        return this._user.asObservable();
    }

    /**
     * Update the user
     *
     * @param user
     */
    update(user: User): Observable<any> {
        return this._user.asObservable();
    }
}
